/**
 * Tracking GA4 / GTM — events e-commerce natifs poussés dans window.dataLayer.
 *
 * Conçu pour être INCASSABLE : chaque fonction est encapsulée dans un try/catch
 * et ne lève jamais. Le tunnel de paiement appelle ces helpers, il ne doit
 * JAMAIS être interrompu par une erreur de tracking.
 *
 * Activation pilotée par le back-office (section « Tracking » du plugin) via
 * window.mondayMindbody.tracking.enabled. Le conteneur GTM lui-même est chargé
 * côté WordPress (wp_head, voir Frontend.php) à partir du même réglage ; ces
 * helpers ne font que pousser des events propres dans le dataLayer, que GTM
 * (et GA4) consomment ensuite.
 *
 * Format : convention GA4 « ecommerce » (https://developers.google.com/
 * analytics/devguides/collection/ga4/ecommerce). On reset `ecommerce` à null
 * avant chaque event pour éviter que des items d'un event précédent ne fuient
 * dans le suivant.
 */

interface Ga4Item {
  item_id?: string;
  item_name?: string;
  item_category?: string;
  item_variant?: string;
  /** Taxonomie d'offre Monday : trial | welcome_offer | credit | package | subscription. */
  offer_type?: string;
  /** Durée d'engagement en mois (abonnements uniquement). */
  commitment_months?: number;
  price?: number;
  quantity?: number;
}

// Les produits manipulés côté front (Service, MindbodyContract, GiftCard) n'ont
// pas une forme commune stricte. On lit défensivement les champs qui existent.
type AnyProduct = Record<string, unknown>;

const CURRENCY = 'EUR';

function getTrackingConfig():
  { enabled?: boolean; gtmId?: string; ga4Id?: string; customerData?: boolean; purchaseDedup?: boolean } | undefined {
  if (typeof mondayMindbody === 'undefined') return undefined;
  return mondayMindbody?.tracking;
}

function isEnabled(): boolean {
  return getTrackingConfig()?.enabled === true;
}

/** Option BO « Données client dans purchase » (Enhanced Conversions). */
function isCustomerDataEnabled(): boolean {
  return getTrackingConfig()?.customerData === true;
}

/** Option BO « Une seule émission de purchase par transaction ». */
function isPurchaseDedupEnabled(): boolean {
  return getTrackingConfig()?.purchaseDedup === true;
}

function getDataLayer(): unknown[] | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { dataLayer?: unknown[] };
  if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
  return w.dataLayer as unknown[];
}

/** Nom de marque (Dynamo / Riise / Punch) injecté par Frontend.php. */
function affiliation(): string | undefined {
  if (typeof mondayMindbody === 'undefined') return undefined;
  return mondayMindbody?.customization?.brandName || undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

function str(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return String(value);
}

/** Push d'un event e-commerce, avec reset préalable de l'objet `ecommerce`. */
function pushEcommerce(event: string, ecommerce: Record<string, unknown>, extra?: Record<string, unknown>): void {
  try {
    if (!isEnabled()) return;
    const dl = getDataLayer();
    if (!dl) return;
    // La marque (affiliation) est portée par TOUS les events e-commerce, pas
    // seulement purchase, pour que les rapports par marque soient cohérents.
    const aff = affiliation();
    if (aff && ecommerce.affiliation === undefined) ecommerce.affiliation = aff;
    const reset: Record<string, unknown> = { ecommerce: null };
    // GTM garde les valeurs du data model d'un event à l'autre : les clés
    // additionnelles (customer…) doivent être resetées comme `ecommerce` pour
    // ne pas fuiter dans les events suivants.
    if (extra) for (const key of Object.keys(extra)) reset[key] = null;
    dl.push(reset);
    dl.push({ event, ecommerce, ...extra });
  } catch {
    // Le tracking ne doit jamais casser le parcours utilisateur.
  }
}

/**
 * Taxonomie d'offre Monday dérivée des champs Mindbody (validée sur données
 * réelles) :
 *  - Contract (AutopaySchedule/AutopayEnabled) → `subscription`
 *  - Service IsIntroOffer : Count>1 → `welcome_offer` (offre de bienvenue),
 *    sinon `trial` (offre d'essai)
 *  - Service hors intro : Count>1 → `package` (packs de séances), sinon
 *    `credit` (séance à l'unité)
 *  - Gift card → hors taxonomie (undefined)
 */
function offerType(p: Record<string, unknown>): string | undefined {
  if (
    p.type === 'contract' ||
    p.AutopaySchedule !== undefined ||
    p.AutopayEnabled !== undefined ||
    p.NumberOfAutopays !== undefined
  ) {
    return 'subscription';
  }
  if (p.type === 'giftcard' || p.CardValue !== undefined) {
    return undefined;
  }
  const isIntro = p.IsIntroOffer === true;
  const count = num(p.Count) ?? 1;
  if (isIntro) return count > 1 ? 'welcome_offer' : 'trial';
  return count > 1 ? 'package' : 'credit';
}

/** Engagement en mois d'un abonnement, dérivé de l'AutopaySchedule Mindbody. */
function commitmentMonths(p: Record<string, unknown>): number | undefined {
  const n = num(p.NumberOfAutopays);
  const sch = p.AutopaySchedule as Record<string, unknown> | undefined;
  if (n === undefined || !sch) return undefined;
  const unit = str(sch.FrequencyTimeUnit);
  const val = num(sch.FrequencyValue) ?? 1;
  if (unit === 'Monthly') return n * val;
  if (unit === 'Weekly') return Math.max(1, Math.round((n * val) / 4.345));
  return undefined;
}

/** Mappe un produit (Service / Contract / GiftCard) vers un item GA4. */
function toItem(product: AnyProduct | undefined | null): Ga4Item {
  const item: Ga4Item = { quantity: 1 };
  if (!product) return item;
  try {
    const p = product as Record<string, unknown>;
    item.item_id = str(p.Id);
    item.item_name = str(p.Name);
    // item_category = la marque (Dynamo / Punch / RIISE), de façon cohérente sur
    // tous les events (demande intégrateur tracking).
    item.item_category = affiliation();
    item.item_variant = str(p.Type);
    item.price = num(p.Price) ?? num(p.SalePrice) ?? num(p.CardValue);
    const ot = offerType(p);
    if (ot) item.offer_type = ot;
    const cm = commitmentMonths(p);
    if (cm !== undefined) item.commitment_months = cm;
  } catch {
    // mapping défensif : on renvoie ce qu'on a pu lire.
  }
  return item;
}

export function trackViewItemList(products: AnyProduct[] | undefined, listName = 'Tarifs'): void {
  if (!products || products.length === 0) return;
  pushEcommerce('view_item_list', {
    item_list_name: listName,
    items: products.map((p) => toItem(p)),
  });
}

export function trackSelectItem(product: AnyProduct, listName = 'Tarifs'): void {
  pushEcommerce('select_item', {
    item_list_name: listName,
    items: [toItem(product)],
  });
}

export function trackAddToCart(product: AnyProduct, value?: number): void {
  const item = toItem(product);
  pushEcommerce('add_to_cart', {
    currency: CURRENCY,
    value: value ?? item.price ?? 0,
    items: [item],
  });
}

export function trackBeginCheckout(product: AnyProduct, value?: number): void {
  const item = toItem(product);
  pushEcommerce('begin_checkout', {
    currency: CURRENCY,
    value: value ?? item.price ?? 0,
    items: [item],
  });
}

export function trackSignUp(method = 'mindbody'): void {
  try {
    if (!isEnabled()) return;
    const dl = getDataLayer();
    if (!dl) return;
    dl.push({ event: 'sign_up', method });
  } catch {
    // noop : voir commentaire pushEcommerce.
  }
}

// ---------------------------------------------------------------------------
// Bloc `customer` du purchase (option BO « Données client dans purchase »).
//
// Poussé en clé racine de l'event purchase (à côté d'`ecommerce`), au format
// convenu avec l'intégrateur tracking : email, phone_number (E.164 best
// effort), first_name, last_name. Sert à l'Enhanced Conversions Google et à
// l'Advanced Matching Meta via GTM. Ne change RIEN tant que l'option BO est
// décochée (les events restent identiques octet pour octet).
// ---------------------------------------------------------------------------
export interface CustomerInput {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Normalisation E.164 best effort d'un téléphone tel qu'il circule dans le
 * tunnel : Mindbody renvoie des numéros nationaux (« 0612345678 »), le
 * formulaire checkout préfixe « 00 », certains profils portent déjà un
 * indicatif. Clientèle française : un national à 10 chiffres devient +33.
 * Si le format n'est pas reconnu, on renvoie les chiffres tels quels.
 */
function toE164(phone: string | undefined): string | undefined {
  if (!phone) return undefined;
  let digits = phone.replace(/\D/g, '');
  if (phone.trim().startsWith('+')) return digits ? `+${digits}` : undefined;
  if (digits.startsWith('00')) digits = digits.slice(2);
  // Numéro déjà international mais précédé d'un zéro parasite saisi à la main
  // (« 033675803567 ») : constaté sur des fiches clients réelles.
  if (/^033\d{9}$/.test(digits)) digits = digits.slice(1);
  if (/^0\d{9}$/.test(digits)) return `+33${digits.slice(1)}`;
  if (/^[1-9]\d{9,14}$/.test(digits)) return `+${digits}`;
  return digits || undefined;
}

/** Construit le bloc customer poussé dans l'event ; undefined si tout est vide. */
function buildCustomer(input: CustomerInput | undefined): Record<string, unknown> | undefined {
  if (!input) return undefined;
  try {
    const customer: Record<string, unknown> = {};
    const email = str(input.email)?.trim().toLowerCase();
    if (email) customer.email = email;
    const phone = toE164(str(input.phone));
    if (phone) customer.phone_number = phone;
    const firstName = str(input.firstName)?.trim();
    if (firstName) customer.first_name = firstName;
    const lastName = str(input.lastName)?.trim();
    if (lastName) customer.last_name = lastName;
    return Object.keys(customer).length > 0 ? customer : undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Dédup du purchase (option BO « Une seule émission par transaction »).
//
// Garde locale par transaction_id, persistée en localStorage (repli
// sessionStorage) : un rechargement de la page de confirmation ou un
// remontage du callback ne re-pousse pas le même purchase. Fail open : si le
// storage est indisponible, on pousse (rater une conversion coûte plus cher
// qu'un doublon). Ne change rien tant que l'option BO est décochée.
// ---------------------------------------------------------------------------
const PUSHED_TX_KEY = 'mmb_ga4_pushed_tx';
const PUSHED_TX_MAX = 20;

function readPushedTransactions(): string[] {
  const raw = readPendingStore(PUSHED_TX_KEY);
  if (!raw) return [];
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function rememberPushedTransaction(txId: string): void {
  try {
    const list = readPushedTransactions();
    list.push(txId);
    const payload = JSON.stringify(list.slice(-PUSHED_TX_MAX));
    try {
      window.localStorage?.setItem(PUSHED_TX_KEY, payload);
    } catch {
      /* noop */
    }
    try {
      window.sessionStorage?.setItem(PUSHED_TX_KEY, payload);
    } catch {
      /* noop */
    }
  } catch {
    /* noop */
  }
}

export interface PurchaseParams {
  /** Id de transaction réel (ShoppingCart.Id Mindbody, ou payment id Alma). */
  transactionId: string | number | undefined;
  value: number;
  /** Produit acheté (utilisé pour construire `items` si `items` n'est pas fourni). */
  product?: AnyProduct | null;
  /** Items GA4 déjà construits (prioritaire sur `product`). */
  items?: Ga4Item[];
  /** 'Credit Card' | 'Alma' | 'Comp' | 'Gift Card'… */
  paymentType?: string;
  /** Code promo éventuellement utilisé. */
  coupon?: string;
  /** Identité de l'acheteur ; ignorée tant que l'option BO customer est décochée. */
  customer?: CustomerInput;
}

export function trackPurchase({
  transactionId,
  value,
  product,
  items,
  paymentType,
  coupon,
  customer,
}: PurchaseParams): void {
  try {
    const txId = str(transactionId);
    if (isPurchaseDedupEnabled() && txId) {
      if (readPushedTransactions().includes(txId)) return;
      rememberPushedTransaction(txId);
    }
    const ecommerce: Record<string, unknown> = {
      transaction_id: txId,
      currency: CURRENCY,
      value,
      items: items ?? (product ? [toItem(product)] : []),
    };
    // affiliation (marque) est injectée par pushEcommerce pour tous les events.
    if (paymentType) ecommerce.payment_type = paymentType;
    if (coupon) ecommerce.coupon = coupon;
    const customerBlock = isCustomerDataEnabled() ? buildCustomer(customer) : undefined;
    pushEcommerce('purchase', ecommerce, customerBlock ? { customer: customerBlock } : undefined);
  } catch {
    // Le tracking ne doit jamais casser le parcours utilisateur.
  }
}

/** Construit un item GA4 minimal à partir d'un identifiant de service seul. */
export function itemFromServiceId(serviceId: unknown, price?: number): Ga4Item {
  return { item_id: str(serviceId), price: num(price), quantity: 1 };
}

// ---------------------------------------------------------------------------
// Persistance de l'item complet à travers une redirection de paiement.
//
// Sur les tunnels 3DS (SCA) et Alma, le purchase est tracké au RETOUR (callback
// après redirection), où seul l'id de service/contrat survit en session. Sans
// aide, l'item du purchase se réduisait à { item_id, quantity } avec price
// undefined. On stocke donc l'item complet (name, category, variant, offer_type,
// price) AVANT la redirection, pour le réutiliser au retour. Best-effort :
// session + localStorage (comme le paiement SCA), et repli sur l'item minimal.
// ---------------------------------------------------------------------------
const PENDING_ITEM_KEY = 'mmb_ga4_pending_item';

function readPendingStore(key: string): string | null {
  try {
    const v = window.sessionStorage?.getItem(key);
    if (v != null) return v;
  } catch {
    /* storage indisponible */
  }
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function clearPendingStore(key: string): void {
  try {
    window.sessionStorage?.removeItem(key);
  } catch {
    /* noop */
  }
  try {
    window.localStorage?.removeItem(key);
  } catch {
    /* noop */
  }
}

/** À appeler AVANT une redirection de paiement (SCA/Alma) avec le produit acheté. */
export function stashPurchaseItem(product: AnyProduct | undefined | null): void {
  try {
    const payload = JSON.stringify(toItem(product));
    try {
      window.sessionStorage?.setItem(PENDING_ITEM_KEY, payload);
    } catch {
      /* noop */
    }
    try {
      window.localStorage?.setItem(PENDING_ITEM_KEY, payload);
    } catch {
      /* noop */
    }
  } catch {
    /* noop */
  }
}

// Même mécanique pour le bloc customer : au retour d'une redirection, le
// contexte utilisateur (email, nom…) n'est plus forcément monté ; on stocke
// l'identité AVANT la redirection pour la réutiliser au purchase du callback.
// PII en storage : on ne stocke que si l'option BO customer est cochée, et la
// lecture est destructive (consommée au premier purchase).
const PENDING_CUSTOMER_KEY = 'mmb_ga4_pending_customer';

/** À appeler AVANT une redirection de paiement (SCA/Alma) avec l'identité de l'acheteur. */
export function stashPurchaseCustomer(customer: CustomerInput | undefined): void {
  try {
    if (!isCustomerDataEnabled() || !customer) return;
    const payload = JSON.stringify(customer);
    try {
      window.sessionStorage?.setItem(PENDING_CUSTOMER_KEY, payload);
    } catch {
      /* noop */
    }
    try {
      window.localStorage?.setItem(PENDING_CUSTOMER_KEY, payload);
    } catch {
      /* noop */
    }
  } catch {
    /* noop */
  }
}

/**
 * À appeler au RETOUR d'une redirection : rend l'identité stockée avant la
 * redirection (lecture destructive), ou le repli fourni (données du paiement
 * restaurées en session) si rien n'a survécu.
 */
export function purchaseCustomer(fallback?: CustomerInput): CustomerInput | undefined {
  try {
    const raw = readPendingStore(PENDING_CUSTOMER_KEY);
    if (raw) {
      clearPendingStore(PENDING_CUSTOMER_KEY);
      const parsed = JSON.parse(raw) as CustomerInput;
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    /* repli */
  }
  return fallback;
}

/**
 * À appeler au RETOUR d'une redirection pour reconstruire l'item du purchase :
 * réutilise l'item complet stocké (price forcé au montant réellement débité),
 * ou retombe sur l'item minimal si rien n'a survécu.
 */
export function purchaseItem(fallbackId: unknown, price?: number): Ga4Item {
  const raw = readPendingStore(PENDING_ITEM_KEY);
  if (raw) {
    try {
      const item = JSON.parse(raw) as Ga4Item;
      clearPendingStore(PENDING_ITEM_KEY);
      if (item.item_id || item.item_name) {
        if (price !== undefined) item.price = price;
        item.quantity = item.quantity ?? 1;
        return item;
      }
    } catch {
      /* JSON corrompu : repli */
    }
  }
  return itemFromServiceId(fallbackId, price);
}

// ---------------------------------------------------------------------------
// Tunnel de réservation (events custom, hors e-commerce GA4).
//
// Le tunnel d'achat est couvert par les events e-commerce ci-dessus ; la
// réservation d'un cours n'avait aucun event (demande de Daphné du 12/10/2026 :
// taux de conversion et abandons du tunnel de réservation). Étapes :
//   view_planning      planning affiché (cours de la semaine / du jour chargés)
//   view_class         fiche d'un cours affichée
//   begin_booking      demande de réservation envoyée au serveur
//   book_class         réservation confirmée par Mindbody
//   book_class_failed  réservation refusée (error_code Mindbody)
// Les paramètres sont portés par l'objet `booking`, reset avant chaque event
// comme `ecommerce` (GTM garde le data model d'un event à l'autre). Liste
// blanche : identifiants Mindbody du cours, nom du cours, studio, coach,
// horaire, site, marque. Jamais de donnée client.

function field(obj: unknown, key: string): unknown {
  return obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[key] : undefined;
}

function currentSiteId(): string | undefined {
  if (typeof mondayMindbody === 'undefined') return undefined;
  return str(mondayMindbody?.siteId);
}

function compact(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) if (value !== undefined) out[key] = value;
  return out;
}

/** Paramètres communs d'un cours (MindbodyClass ou cours public du planning, lus défensivement). */
function classBookingParams(cls: unknown): Record<string, unknown> {
  const description = field(cls, 'ClassDescription');
  const location = field(cls, 'Location');
  return compact({
    class_id: str(field(cls, 'Id')),
    class_schedule_id: str(field(cls, 'ClassScheduleId')),
    class_name: str(field(description, 'Name')),
    class_start: str(field(cls, 'StartDateTime')),
    studio: str(field(location, 'Name')),
    coach: str(field(field(cls, 'Staff'), 'Name')),
    site_id: str(field(location, 'SiteID')) ?? currentSiteId(),
    affiliation: affiliation(),
  });
}

function pushBooking(event: string, booking: Record<string, unknown>): void {
  try {
    if (!isEnabled()) return;
    const dl = getDataLayer();
    if (!dl) return;
    dl.push({ booking: null });
    dl.push({ event, booking });
  } catch {
    // noop : voir commentaire pushEcommerce.
  }
}

// Une vue par planning chargé et par fiche cours, pour la durée de la page :
// les relectures React Query (focus, après réservation) ne recomptent pas.
const seenViews = new Set<string>();

/** Planning affiché. `key` identifie la vue (site + semaine / jour + filtres), `count` le nombre de cours chargés. */
export function trackViewPlanning({ key, count }: { key: string; count?: number }): void {
  try {
    const seenKey = `planning:${key}`;
    if (seenViews.has(seenKey)) return;
    seenViews.add(seenKey);
    pushBooking(
      'view_planning',
      compact({ site_id: currentSiteId(), affiliation: affiliation(), class_count: num(count) }),
    );
  } catch {
    // noop : voir commentaire pushEcommerce.
  }
}

/** Fiche d'un cours affichée (connecté ou non). */
export function trackViewClass(cls: unknown): void {
  try {
    const id = str(field(cls, 'Id'));
    if (!id) return;
    const seenKey = `class:${id}`;
    if (seenViews.has(seenKey)) return;
    seenViews.add(seenKey);
    pushBooking('view_class', classBookingParams(cls));
  } catch {
    // noop : voir commentaire pushEcommerce.
  }
}

export interface BookingTrackOptions {
  /** Inscription en liste d'attente plutôt que réservation. */
  waitlist?: boolean;
  /** Forfait choisi pour débiter la séance (undefined : choix laissé au serveur). */
  serviceId?: unknown;
  /** Cours à inscription (enrollment) plutôt que séance. */
  course?: boolean;
  /** Réservation Run Club (chemin dédié, sans forfait). */
  runClub?: boolean;
}

function bookingOptions(options: BookingTrackOptions): Record<string, unknown> {
  return compact({
    booking_type: options.runClub ? 'runclub' : options.course ? 'course' : 'class',
    waitlist: options.waitlist === true,
    service_id: str(options.serviceId),
  });
}

/** Demande de réservation envoyée au serveur (clic « Réserver » validé). */
export function trackBeginBooking(cls: unknown, options: BookingTrackOptions = {}): void {
  pushBooking('begin_booking', { ...classBookingParams(cls), ...bookingOptions(options) });
}

/** Réservation confirmée par Mindbody. */
export function trackBookClass(cls: unknown, options: BookingTrackOptions = {}): void {
  pushBooking('book_class', { ...classBookingParams(cls), ...bookingOptions(options) });
}

/** Réservation refusée : `errorCode` = ErrorCode Mindbody (ou 'unknown'). */
export function trackBookClassFailed(cls: unknown, errorCode: unknown, options: BookingTrackOptions = {}): void {
  pushBooking('book_class_failed', {
    ...classBookingParams(cls),
    ...bookingOptions(options),
    error_code: str(errorCode) ?? 'unknown',
  });
}
